/** @jsxImportSource theme-ui @jsxRuntime classic */
import { H } from '@hzdg/sectioning';
import { Flex, Box, Link, useScrollTo } from '@tishman/components';
import React, { SyntheticEvent, useEffect, useRef, forwardRef } from 'react';

import {
  actions,
  selectWizardCurrentStep,
  useAppDispatch,
  useAppSelector,
} from '../../store';
import { newRelic } from '../../utils/new-relic';

import { StepContainer } from './StepContainer';

import { ModalLoader, useCombinedRefs } from '.';

export interface StepProps {
  'data-section'?: string;
  'title': string;
  'summary'?: React.ReactNode;
  'stepNumber': number;
  'children': React.ReactNode;
  'showLoadingModal'?: boolean;
  'width'?: 'default' | 'wide';
  'isSmallerFontSize'?: boolean;
  'stepIsValid'?: boolean;
}

/** Step component renders the component used in that step
 * updates the step state and handles the step summary.
 */

export const Step = forwardRef<HTMLDivElement, StepProps>(
  (
    {
      children,
      title,
      stepNumber,
      summary,
      showLoadingModal,
      stepIsValid = false,
      width = 'default',
      isSmallerFontSize,
      ...props
    }: StepProps,
    ref,
  ): React.JSX.Element => {
    const dispatch = useAppDispatch();

    const currentStep = useAppSelector(selectWizardCurrentStep);

    const scrollTo = useScrollTo({
      forceAnimation: true,
      offset: { x: 0, y: -50 },
    });

    const innerRef = useRef<HTMLDivElement>(null);
    const combinedRef = useCombinedRefs(ref, innerRef);

    useEffect(() => {
      if (currentStep === stepNumber && stepNumber !== 1 && combinedRef.current)
        scrollTo(combinedRef.current);
    }, [currentStep, scrollTo, stepNumber]);

    const nextStep = (e: React.SyntheticEvent) => {
      e.preventDefault();
    };

    // Block scrolling while modal active
    useEffect(() => {
      if (showLoadingModal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    }, [showLoadingModal]);

    return (
      <StepContainer ref={ref} stepNumber={stepNumber} width={width} {...props}>
        {showLoadingModal && <ModalLoader />}
        <Link
          aria-disabled={!stepIsValid}
          aria-label={`Click to go to ${title} step`}
          data-gtm-trigger="header"
          href="#"
          // a11y:replace spaces with dashes and make lowercase
          id={title?.replace(/\s/g, '-').toLowerCase()}
          onClick={nextStep}
          sx={{
            'cursor':
              stepNumber === currentStep
                ? 'default'
                : stepIsValid
                ? 'pointer'
                : 'not-allowed',
            'textDecoration': 'none',
            ':hover': {
              textDecoration: 'none',
            },
            ':hover h2': stepIsValid
              ? {
                  color: 'primary',
                }
              : {},
          }}
          tabIndex={stepIsValid ? 0 : -1}
        >
          <H
            sx={{
              maxWidth: 347,
              variant: isSmallerFontSize
                ? 'text.smallTitle'
                : 'text.mediumTitle',
              mb: summary || stepNumber === currentStep ? 3 : 0,
              color: !summary
                ? currentStep < stepNumber
                  ? 'backgroundDisabled'
                  : 'initial'
                : 'initial',
            }}
          >
            {title}
          </H>
        </Link>
        {stepNumber === currentStep ? (
          children
        ) : (
          <Flex
            sx={{
              justifyContent: 'space-between',
              flexDirection: ['column', 'row'],
              gap: ['10px', '0px'],
            }}
          >
            <Box>{summary}</Box>
            {summary && (
              <Link
                href="#"
                onClick={(e: SyntheticEvent) => {
                  e.preventDefault();
                  newRelic.logCartAction(
                    `BuyTickets Step - Edit clicked in ${title} Step summary`,
                    'Edit',
                  );
                  dispatch(actions.wizard.setCurrentStep(stepNumber));
                }}
                sx={{ variant: 'links.inline', fontWeight: 'medium' }}
              >
                Edit
              </Link>
            )}
          </Flex>
        )}
      </StepContainer>
    );
  },
);

Step.displayName = 'Step';
